import React, { useEffect, useState } from 'react';
import AvatarCircle from '../../../components/common/AvatarCircle/AvatarCircle';
import SummaryPills from '../../../components/common/SummaryPills/SummaryPills';
import { StudentService } from '../../../services';
import type { Student } from '../../../types';
import { formatBirthDateWithAge, formatText, formatWeight } from '../../../utils';
import styles from './StudentTrainingModalHeader.module.css';

interface StudentTrainingModalHeaderProps {
  studentId: string;
}

interface StudentHeaderInfo {
  name: string;
  birthDate: string;
  weight: string;
  coachName: string;
}

const EMPTY_HEADER: StudentHeaderInfo = {
  name: '',
  birthDate: 'Não informado',
  weight: 'Não informado',
  coachName: 'Sem professora',
};

const buildHeaderInfo = (
  student: Student | null,
  coachName: string | null,
): StudentHeaderInfo => ({
  name: student?.full_name?.trim() || EMPTY_HEADER.name,
  birthDate: formatBirthDateWithAge(student?.birth_date),
  weight: student?.weight_kg != null ? formatWeight(student.weight_kg) : EMPTY_HEADER.weight,
  coachName: formatText(coachName, EMPTY_HEADER.coachName),
});

const StudentTrainingModalHeader: React.FC<StudentTrainingModalHeaderProps> = ({
  studentId,
}) => {
  const [info, setInfo] = useState<StudentHeaderInfo>(EMPTY_HEADER);

  useEffect(() => {
    const numericId = Number(studentId);

    if (!Number.isInteger(numericId) || numericId <= 0) {
      setInfo(EMPTY_HEADER);
      return undefined;
    }

    let isMounted = true;

    const load = async () => {
      const [student, coach] = await Promise.all([
        StudentService.findById(numericId).catch(() => null),
        StudentService.getCoachAssociation(numericId).catch(() => ({ coach_name: null })),
      ]);

      if (!isMounted) {
        return;
      }

      setInfo(buildHeaderInfo(student, coach.coach_name));
    };

    void load();

    return () => {
      isMounted = false;
    };
  }, [studentId]);

  return (
    <header className={styles.header}>
      <AvatarCircle name={info.name} size="md" />
      <div className={styles.identity}>
        <h2 className={styles.name}>{info.name}</h2>
        <SummaryPills
          className={styles.facts}
          items={[
            { label: 'Nascimento', value: info.birthDate },
            { label: 'Peso', value: info.weight },
            { label: 'Professora', value: info.coachName },
          ]}
        />
      </div>
    </header>
  );
};

export default StudentTrainingModalHeader;
